import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Bell, Save, Loader2, CheckCircle2, AlertCircle, Info, Link, Image, ShoppingCart, Tag, Trash2, Plus } from 'lucide-react';
import { UserProfile } from '../../../types';

interface OrderUpdateTabProps {
  user: UserProfile;
}

interface NotificationSettings {
  // Order Placed - Prepaid
  order_placed_prepaid_enabled: boolean;
  order_placed_prepaid_template: string;
  order_placed_prepaid_variables: string[];
  order_placed_prepaid_button_url: string;
  order_placed_prepaid_has_image: boolean;
  
  // Order Placed - COD
  order_placed_cod_enabled: boolean;
  order_placed_cod_template: string;
  order_placed_cod_variables: string[];
  order_placed_cod_button_url: string;
  order_placed_cod_has_image: boolean;
  
  // Order Fulfilled
  order_fulfilled_enabled: boolean;
  order_fulfilled_template: string;
  order_fulfilled_variables: string[];
  order_fulfilled_button_url: string;
  order_fulfilled_has_image: boolean;
  
  // Order Delivered
  order_delivered_enabled: boolean;
  order_delivered_template: string;
  order_delivered_variables: string[];
  order_delivered_button_url: string;
  order_delivered_has_image: boolean;
  
  // Order Cancelled
  order_cancelled_enabled: boolean;
  order_cancelled_template: string;
  order_cancelled_variables: string[];
  order_cancelled_button_url: string;
  order_cancelled_has_image: boolean;
  order_cancelled_notes_fallback: string;
  
  // Abandoned Cart
  abandoned_cart_enabled: boolean;
  abandoned_cart_template: string;
  abandoned_cart_variables: string[];
  abandoned_cart_button_url: string;
  abandoned_cart_has_image: boolean;
  abandoned_cart_delay_minutes: number;
}

interface TagNotification {
  id?: string;
  tag_name: string;
  template_name: string;
  variables: string[];
  button_url: string;
  has_image: boolean;
  notes_fallback: string;
  enabled: boolean;
}

const AVAILABLE_VARIABLES = [
  { value: 'customer_name', label: 'Customer Name' },
  { value: 'order_number', label: 'Order Number' },
  { value: 'order_id', label: 'Order ID' },
  { value: 'total_price', label: 'Total Amount' },
  { value: 'items_list', label: 'Items List' },
  { value: 'payment_method', label: 'Payment Method (COD/Prepaid)' },
  { value: 'tracking_number', label: 'Tracking Number' },
  { value: 'tracking_url', label: 'Tracking URL' },
  { value: 'order_status_url', label: 'Order Status Page' },
  { value: 'fulfillment_status', label: 'Fulfillment Status' },
  { value: 'order_notes', label: 'Order Notes / Cancellation Reason' },
  { value: 'customer_notes', label: 'Customer Notes' },
  { value: 'cart_value', label: 'Cart Value' },
  { value: 'cart_url', label: 'Cart Recovery URL' },
];

const AVAILABLE_BUTTON_VARIABLES = [
  { value: 'order_status_url', label: 'Order Status Page' },
  { value: 'tracking_url', label: 'Tracking URL' },
  { value: 'cart_url', label: 'Cart Recovery URL' },
];

const DELAY_OPTIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
];

export const OrderUpdateTab: React.FC<OrderUpdateTabProps> = ({ user }) => {
  const [masterEnabled, setMasterEnabled] = useState(true);
  const [settings, setSettings] = useState<NotificationSettings>({
    order_placed_prepaid_enabled: true,
    order_placed_prepaid_template: 'order_placed_prepaid',
    order_placed_prepaid_variables: ['customer_name', 'order_number', 'total_price'],
    order_placed_prepaid_button_url: 'order_status_url',
    order_placed_prepaid_has_image: true,
    
    order_placed_cod_enabled: true,
    order_placed_cod_template: 'order_placed_cod',
    order_placed_cod_variables: ['customer_name', 'order_number', 'total_price'],
    order_placed_cod_button_url: 'order_status_url',
    order_placed_cod_has_image: true,
    
    order_fulfilled_enabled: true,
    order_fulfilled_template: 'order_fulfilled',
    order_fulfilled_variables: ['customer_name', 'order_number', 'tracking_number'],
    order_fulfilled_button_url: 'tracking_url',
    order_fulfilled_has_image: false,
    
    order_delivered_enabled: true,
    order_delivered_template: 'order_delivered',
    order_delivered_variables: ['customer_name', 'order_number'],
    order_delivered_button_url: 'order_status_url',
    order_delivered_has_image: false,
    
    order_cancelled_enabled: false,
    order_cancelled_template: 'order_cancelled',
    order_cancelled_variables: ['customer_name', 'order_number', 'order_notes'],
    order_cancelled_button_url: '',
    order_cancelled_has_image: false,
    order_cancelled_notes_fallback: 'Please contact support for details',
    
    abandoned_cart_enabled: false,
    abandoned_cart_template: 'abandoned_cart',
    abandoned_cart_variables: ['customer_name', 'cart_value', 'cart_url'],
    abandoned_cart_button_url: 'cart_url',
    abandoned_cart_has_image: true,
    abandoned_cart_delay_minutes: 60,
  });
  
  const [tagNotifications, setTagNotifications] = useState<TagNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    loadSettings();
    loadTagNotifications();
  }, [user.id]);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('admin_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading settings:', error);
        return;
      }

      if (data) {
        setSettings({
          order_placed_prepaid_enabled: data.order_placed_prepaid_enabled ?? true,
          order_placed_prepaid_template: data.order_placed_prepaid_template || 'order_placed_prepaid',
          order_placed_prepaid_variables: data.order_placed_prepaid_variables || ['customer_name', 'order_number', 'total_price'],
          order_placed_prepaid_button_url: data.order_placed_prepaid_button_url || 'order_status_url',
          order_placed_prepaid_has_image: data.order_placed_prepaid_has_image ?? true,
          
          order_placed_cod_enabled: data.order_placed_cod_enabled ?? true,
          order_placed_cod_template: data.order_placed_cod_template || 'order_placed_cod',
          order_placed_cod_variables: data.order_placed_cod_variables || ['customer_name', 'order_number', 'total_price'],
          order_placed_cod_button_url: data.order_placed_cod_button_url || 'order_status_url',
          order_placed_cod_has_image: data.order_placed_cod_has_image ?? true,
          
          order_fulfilled_enabled: data.order_fulfilled_enabled ?? true,
          order_fulfilled_template: data.order_fulfilled_template || 'order_fulfilled',
          order_fulfilled_variables: data.order_fulfilled_variables || ['customer_name', 'order_number', 'tracking_number'],
          order_fulfilled_button_url: data.order_fulfilled_button_url || 'tracking_url',
          order_fulfilled_has_image: data.order_fulfilled_has_image ?? false,
          
          order_delivered_enabled: data.order_delivered_enabled ?? true,
          order_delivered_template: data.order_delivered_template || 'order_delivered',
          order_delivered_variables: data.order_delivered_variables || ['customer_name', 'order_number'],
          order_delivered_button_url: data.order_delivered_button_url || 'order_status_url',
          order_delivered_has_image: data.order_delivered_has_image ?? false,
          
          order_cancelled_enabled: data.order_cancelled_enabled ?? false,
          order_cancelled_template: data.order_cancelled_template || 'order_cancelled',
          order_cancelled_variables: data.order_cancelled_variables || ['customer_name', 'order_number', 'order_notes'],
          order_cancelled_button_url: data.order_cancelled_button_url || '',
          order_cancelled_has_image: data.order_cancelled_has_image ?? false,
          order_cancelled_notes_fallback: data.order_cancelled_notes_fallback || 'Please contact support for details',
          
          abandoned_cart_enabled: data.abandoned_cart_enabled ?? false,
          abandoned_cart_template: data.abandoned_cart_template || 'abandoned_cart',
          abandoned_cart_variables: data.abandoned_cart_variables || ['customer_name', 'cart_value', 'cart_url'],
          abandoned_cart_button_url: data.abandoned_cart_button_url || 'cart_url',
          abandoned_cart_has_image: data.abandoned_cart_has_image ?? true,
          abandoned_cart_delay_minutes: data.abandoned_cart_delay_minutes ?? 60,
        });

        const anyEnabled = data.order_placed_prepaid_enabled || 
                          data.order_placed_cod_enabled || 
                          data.order_fulfilled_enabled || 
                          data.order_delivered_enabled ||
                          data.order_cancelled_enabled ||
                          data.abandoned_cart_enabled;
        setMasterEnabled(anyEnabled ?? true);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTagNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('tag_notifications')
        .select('*')
        .eq('admin_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading tag notifications:', error);
        return;
      }

      if (data) {
        setTagNotifications(data);
      }
    } catch (err) {
      console.error('Failed to load tag notifications:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');

    try {
      const payload = {
        admin_id: user.id,
        ...settings,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('notification_settings')
        .upsert(payload, { 
          onConflict: 'admin_id',
          ignoreDuplicates: false 
        })
        .select();

      if (error) {
        console.error('Upsert error:', error);
        throw error;
      }

      console.log('✅ Saved successfully:', data);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTagNotification = async (tag: TagNotification, index: number) => {
    try {
      if (tag.id) {
        // Update existing
        const { error } = await supabase
          .from('tag_notifications')
          .update({
            tag_name: tag.tag_name,
            template_name: tag.template_name,
            variables: tag.variables,
            button_url: tag.button_url,
            has_image: tag.has_image,
            notes_fallback: tag.notes_fallback,
            enabled: tag.enabled,
            updated_at: new Date().toISOString()
          })
          .eq('id', tag.id);

        if (error) throw error;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('tag_notifications')
          .insert({
            admin_id: user.id,
            tag_name: tag.tag_name,
            template_name: tag.template_name,
            variables: tag.variables,
            button_url: tag.button_url,
            has_image: tag.has_image,
            notes_fallback: tag.notes_fallback,
            enabled: tag.enabled,
          })
          .select()
          .single();

        if (error) throw error;
        
        // Update local state with new id
        const updated = [...tagNotifications];
        updated[index] = { ...tag, id: data.id };
        setTagNotifications(updated);
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save tag notification:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleDeleteTagNotification = async (tagId: string, index: number) => {
    try {
      const { error } = await supabase
        .from('tag_notifications')
        .delete()
        .eq('id', tagId);

      if (error) throw error;

      const updated = tagNotifications.filter((_, i) => i !== index);
      setTagNotifications(updated);
    } catch (error) {
      console.error('Failed to delete tag notification:', error);
    }
  };

  const addTagNotification = () => {
    setTagNotifications([...tagNotifications, {
      tag_name: '',
      template_name: '',
      variables: [],
      button_url: '',
      has_image: false,
      notes_fallback: 'No additional message',
      enabled: true,
    }]);
  };

  const updateTagNotification = (index: number, field: keyof TagNotification, value: any) => {
    const updated = [...tagNotifications];
    updated[index] = { ...updated[index], [field]: value };
    setTagNotifications(updated);
  };

  const addTagVariable = (index: number) => {
    const updated = [...tagNotifications];
    if (updated[index].variables.length < 10) {
      updated[index].variables = [...updated[index].variables, ''];
      setTagNotifications(updated);
    }
  };

  const removeTagVariable = (tagIndex: number, varIndex: number) => {
    const updated = [...tagNotifications];
    updated[tagIndex].variables = updated[tagIndex].variables.filter((_, i) => i !== varIndex);
    setTagNotifications(updated);
  };

  const updateTagVariable = (tagIndex: number, varIndex: number, value: string) => {
    const updated = [...tagNotifications];
    updated[tagIndex].variables[varIndex] = value;
    setTagNotifications(updated);
  };

  const toggleMaster = (enabled: boolean) => {
    setMasterEnabled(enabled);
    setSettings({
      ...settings,
      order_placed_prepaid_enabled: enabled,
      order_placed_cod_enabled: enabled,
      order_fulfilled_enabled: enabled,
      order_delivered_enabled: enabled,
      order_cancelled_enabled: enabled,
      abandoned_cart_enabled: enabled,
    });
  };

  const updateSetting = (key: keyof NotificationSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const addVariable = (eventKey: string) => {
    const variablesKey = `${eventKey}_variables` as keyof NotificationSettings;
    const currentVars = settings[variablesKey] as string[];
    if (currentVars.length < 10) {
      updateSetting(variablesKey, [...currentVars, '']);
    }
  };

  const removeVariable = (eventKey: string, index: number) => {
    const variablesKey = `${eventKey}_variables` as keyof NotificationSettings;
    const currentVars = settings[variablesKey] as string[];
    updateSetting(variablesKey, currentVars.filter((_, i) => i !== index));
  };

  const updateVariable = (eventKey: string, index: number, value: string) => {
    const variablesKey = `${eventKey}_variables` as keyof NotificationSettings;
    const currentVars = [...(settings[variablesKey] as string[])];
    currentVars[index] = value;
    updateSetting(variablesKey, currentVars);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  const events = [
    {
      key: 'order_placed_prepaid',
      label: 'Order Placed - Prepaid',
      description: 'Sent when customer places prepaid order',
      icon: '💳',
      hasNotesFallback: false,
    },
    {
      key: 'order_placed_cod',
      label: 'Order Placed - COD',
      description: 'Sent when customer places COD order',
      icon: '💵',
      hasNotesFallback: false,
    },
    {
      key: 'order_fulfilled',
      label: 'Order Fulfilled',
      description: 'Sent when order is shipped',
      icon: '📦',
      hasNotesFallback: false,
    },
    {
      key: 'order_delivered',
      label: 'Order Delivered',
      description: 'Sent when order is delivered',
      icon: '✅',
      hasNotesFallback: false,
    },
    {
      key: 'order_cancelled',
      label: 'Order Cancelled',
      description: 'Sent when order is cancelled',
      icon: '❌',
      hasNotesFallback: true,
    },
  ];

  return (
    <div className="max-w-5xl mx-auto pb-20 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center sticky top-0 z-20 bg-gray-50/95 backdrop-blur py-4 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Order Update Notifications</h2>
          <p className="text-slate-500 text-sm">Configure WhatsApp notifications for order events</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 font-medium shadow-sm transition-all"
        >
          {saving ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>}
          Save Changes
        </button>
      </div>

      {/* Save Status Message */}
      {saveStatus === 'success' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm animate-in fade-in">
          <CheckCircle2 size={16} />
          Settings saved successfully!
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm animate-in fade-in">
          <AlertCircle size={16} />
          Failed to save settings. Please try again.
        </div>
      )}

      {/* Master Toggle */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="text-indigo-600" size={24} />
            <div>
              <h3 className="font-bold text-slate-800">Enable Order Notifications</h3>
              <p className="text-xs text-slate-500">Master switch to enable/disable all notifications</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={masterEnabled}
              onChange={(e) => toggleMaster(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
        </div>
      </div>

      {/* Order Event Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Bell size={20} />
          Order Events
        </h3>
        
        {events.map(({ key, label, description, icon, hasNotesFallback }) => {
          const enabledKey = `${key}_enabled` as keyof NotificationSettings;
          const templateKey = `${key}_template` as keyof NotificationSettings;
          const variablesKey = `${key}_variables` as keyof NotificationSettings;
          const buttonUrlKey = `${key}_button_url` as keyof NotificationSettings;
          const hasImageKey = `${key}_has_image` as keyof NotificationSettings;
          const notesFallbackKey = hasNotesFallback ? `${key}_notes_fallback` as keyof NotificationSettings : null;
          const variables = settings[variablesKey] as string[];
          
          return (
            <div key={key} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{icon}</span>
                  <div>
                    <h4 className="font-bold text-slate-800">{label}</h4>
                    <p className="text-xs text-slate-500">{description}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings[enabledKey] as boolean}
                    onChange={(e) => updateSetting(enabledKey, e.target.checked)}
                    disabled={!masterEnabled}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
                </label>
              </div>

              {settings[enabledKey] && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-4">
                  {/* Template Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Interakt Template Name
                    </label>
                    <input
                      type="text"
                      value={settings[templateKey] as string}
                      onChange={(e) => updateSetting(templateKey, e.target.value)}
                      placeholder="e.g., order_placed_prepaid"
                      className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      disabled={!masterEnabled}
                    />
                  </div>

                  {/* Has Image Header Toggle */}
                  <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <Image size={18} className="text-purple-700" />
                    <label className="flex-1 text-sm font-medium text-purple-900">
                      Template has image header
                    </label>
                    <input
                      type="checkbox"
                      checked={settings[hasImageKey] as boolean}
                      onChange={(e) => updateSetting(hasImageKey, e.target.checked)}
                      disabled={!masterEnabled}
                      className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                  </div>

                  {/* Notes Fallback (for Order Cancelled) */}
                  {hasNotesFallback && notesFallbackKey && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Fallback Text (if order notes are empty)
                      </label>
                      <input
                        type="text"
                        value={settings[notesFallbackKey] as string}
                        onChange={(e) => updateSetting(notesFallbackKey, e.target.value)}
                        placeholder="e.g., Please contact support"
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                        disabled={!masterEnabled}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        This text will be used when order notes are not available
                      </p>
                    </div>
                  )}

                  {/* Variable Mapping */}
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Template Variables (in order)
                      </label>
                      <button
                        onClick={() => addVariable(key)}
                        disabled={!masterEnabled || variables.length >= 10}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50"
                      >
                        + Add Variable
                      </button>
                    </div>

                    <div className="space-y-2">
                      {variables.map((variable, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-sm font-mono text-slate-600 min-w-[40px]">
                            {`{{${index + 1}}}`}
                          </span>
                          <select
                            value={variable}
                            onChange={(e) => updateVariable(key, index, e.target.value)}
                            disabled={!masterEnabled}
                            className="flex-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                          >
                            <option value="">Select variable</option>
                            {AVAILABLE_VARIABLES.map((v) => (
                              <option key={v.value} value={v.value}>
                                {v.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => removeVariable(key, index)}
                            disabled={!masterEnabled}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {variables.length === 0 && (
                        <p className="text-xs text-slate-500 italic">
                          No variables mapped. Click "+ Add Variable" to add.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Button URL */}
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Link size={16} className="text-amber-700" />
                      <label className="block text-sm font-medium text-amber-900">
                        Button URL Variable
                      </label>
                    </div>
                    <select
                      value={settings[buttonUrlKey] as string}
                      onChange={(e) => updateSetting(buttonUrlKey, e.target.value)}
                      disabled={!masterEnabled}
                      className="w-full p-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm bg-white"
                    >
                      <option value="">No button URL</option>
                      {AVAILABLE_BUTTON_VARIABLES.map((v) => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Abandoned Cart Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <ShoppingCart size={20} />
          Abandoned Cart Recovery
        </h3>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <span className="text-3xl">🛒</span>
              <div>
                <h4 className="font-bold text-slate-800">Abandoned Cart Reminder</h4>
                <p className="text-xs text-slate-500">Send reminder after cart is abandoned</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.abandoned_cart_enabled}
                onChange={(e) => updateSetting('abandoned_cart_enabled', e.target.checked)}
                disabled={!masterEnabled}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50"></div>
            </label>
          </div>

          {settings.abandoned_cart_enabled && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-4">
              {/* Delay Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Send Reminder After
                </label>
                <select
                  value={settings.abandoned_cart_delay_minutes}
                  onChange={(e) => updateSetting('abandoned_cart_delay_minutes', Number(e.target.value))}
                  disabled={!masterEnabled}
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  {DELAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Interakt Template Name
                </label>
                <input
                  type="text"
                  value={settings.abandoned_cart_template}
                  onChange={(e) => updateSetting('abandoned_cart_template', e.target.value)}
                  placeholder="e.g., abandoned_cart"
                  className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  disabled={!masterEnabled}
                />
              </div>

              {/* Has Image Header */}
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Image size={18} className="text-purple-700" />
                <label className="flex-1 text-sm font-medium text-purple-900">
                  Template has image header
                </label>
                <input
                  type="checkbox"
                  checked={settings.abandoned_cart_has_image}
                  onChange={(e) => updateSetting('abandoned_cart_has_image', e.target.checked)}
                  disabled={!masterEnabled}
                  className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                />
              </div>

              {/* Variables */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Template Variables (in order)
                  </label>
                  <button
                    onClick={() => addVariable('abandoned_cart')}
                    disabled={!masterEnabled || settings.abandoned_cart_variables.length >= 10}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50"
                  >
                    + Add Variable
                  </button>
                </div>

                <div className="space-y-2">
                  {settings.abandoned_cart_variables.map((variable, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm font-mono text-slate-600 min-w-[40px]">
                        {`{{${index + 1}}}`}
                      </span>
                      <select
                        value={variable}
                        onChange={(e) => updateVariable('abandoned_cart', index, e.target.value)}
                        disabled={!masterEnabled}
                        className="flex-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                      >
                        <option value="">Select variable</option>
                        {AVAILABLE_VARIABLES.map((v) => (
                          <option key={v.value} value={v.value}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeVariable('abandoned_cart', index)}
                        disabled={!masterEnabled}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button URL */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Link size={16} className="text-amber-700" />
                  <label className="block text-sm font-medium text-amber-900">
                    Button URL Variable
                  </label>
                </div>
                <select
                  value={settings.abandoned_cart_button_url}
                  onChange={(e) => updateSetting('abandoned_cart_button_url', e.target.value)}
                  disabled={!masterEnabled}
                  className="w-full p-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm bg-white"
                >
                  <option value="">No button URL</option>
                  {AVAILABLE_BUTTON_VARIABLES.map((v) => (
                    <option key={v.value} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tag-Based Notifications Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Tag size={20} />
            Tag-Based Notifications
          </h3>
          <button
            onClick={addTagNotification}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          >
            <Plus size={16} />
            Add Tag Configuration
          </button>
        </div>

        {tagNotifications.length === 0 ? (
          <div className="bg-white p-8 rounded-2xl border border-slate-200 text-center">
            <Tag size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No tag-based notifications configured</p>
            <p className="text-xs text-slate-400 mt-1">Click "Add Tag Configuration" to create one</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tagNotifications.map((tag, tagIndex) => (
              <div key={tagIndex} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Shopify Tag Name
                      </label>
                      <input
                        type="text"
                        value={tag.tag_name}
                        onChange={(e) => updateTagNotification(tagIndex, 'tag_name', e.target.value)}
                        placeholder="e.g., VIP"
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Template Name
                      </label>
                      <input
                        type="text"
                        value={tag.template_name}
                        onChange={(e) => updateTagNotification(tagIndex, 'template_name', e.target.value)}
                        placeholder="e.g., vip_customer"
                        className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tag.enabled}
                        onChange={(e) => updateTagNotification(tagIndex, 'enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                    {tag.id && (
                      <button
                        onClick={() => handleDeleteTagNotification(tag.id!, tagIndex)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Has Image Header */}
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                  <Image size={18} className="text-purple-700" />
                  <label className="flex-1 text-sm font-medium text-purple-900">
                    Template has image header
                  </label>
                  <input
                    type="checkbox"
                    checked={tag.has_image}
                    onChange={(e) => updateTagNotification(tagIndex, 'has_image', e.target.checked)}
                    className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                </div>

                {/* Notes Fallback */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fallback Text (if customer notes are empty)
                  </label>
                  <input
                    type="text"
                    value={tag.notes_fallback}
                    onChange={(e) => updateTagNotification(tagIndex, 'notes_fallback', e.target.value)}
                    placeholder="e.g., Welcome message"
                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>

                {/* Variables */}
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Template Variables (in order)
                    </label>
                    <button
                      onClick={() => addTagVariable(tagIndex)}
                      disabled={tag.variables.length >= 10}
                      className="text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50"
                    >
                      + Add Variable
                    </button>
                  </div>

                  <div className="space-y-2">
                    {tag.variables.map((variable, varIndex) => (
                      <div key={varIndex} className="flex items-center gap-2">
                        <span className="text-sm font-mono text-slate-600 min-w-[40px]">
                          {`{{${varIndex + 1}}}`}
                        </span>
                        <select
                          value={variable}
                          onChange={(e) => updateTagVariable(tagIndex, varIndex, e.target.value)}
                          className="flex-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                        >
                          <option value="">Select variable</option>
                          {AVAILABLE_VARIABLES.map((v) => (
                            <option key={v.value} value={v.value}>
                              {v.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => removeTagVariable(tagIndex, varIndex)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Button URL */}
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Link size={16} className="text-amber-700" />
                    <label className="block text-sm font-medium text-amber-900">
                      Button URL Variable
                    </label>
                  </div>
                  <select
                    value={tag.button_url}
                    onChange={(e) => updateTagNotification(tagIndex, 'button_url', e.target.value)}
                    className="w-full p-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm bg-white"
                  >
                    <option value="">No button URL</option>
                    {AVAILABLE_BUTTON_VARIABLES.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Save Button */}
                <button
                  onClick={() => handleSaveTagNotification(tag, tagIndex)}
                  className="w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  {tag.id ? 'Update Configuration' : 'Save Configuration'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 className="font-bold text-blue-900 mb-2">📌 Important Notes:</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc pl-5">
          <li>Template names must match exactly with Interakt dashboard</li>
          <li>Templates must be approved by WhatsApp before use</li>
          <li>Variables must be in the SAME ORDER as your template</li>
          <li>Image header toggle: Enable ONLY if your template has an image header</li>
          <li>Abandoned cart: Requires Shopify webhook for checkouts/create</li>
          <li>Tag-based: Triggered when customer is tagged in Shopify</li>
        </ul>
      </div>
    </div>
  );
};
